import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../data/store";
import * as F from "@soheil/core";
import type { FocusSession, Pose } from "@soheil/core";
import * as wake from "../platform/wakelock";
import { haptic, setClosingConfirmation } from "../platform/telegram";

export type FocusPhase = "idle" | "running" | "paused" | "done" | "break";

/**
 * تایمر همدرس سهیل.
 *
 * زمان همیشه از ساعت دیوار خوانده می‌شود، نه از شمارش داخلی — پس اگر مینی‌اپ
 * بسته شود، صفحه بخوابد یا تب معلق شود، عدد درست برمی‌گردد. setInterval فقط
 * برای رندر دوباره است، نه برای شمردن.
 */
export function useFocus() {
  const { state, dispatch, today } = useStore();
  const [, forceTick] = useState(0);
  const [phase, setPhase] = useState<FocusPhase>("idle");
  const [breakUntil, setBreakUntil] = useState<number | null>(null);
  const microRef = useRef<number | undefined>(undefined);
  const [microPose, setMicroPose] = useState<Pose | null>(null);

  const active: FocusSession | undefined = useMemo(() => {
    const open = state.sessions?.filter((s) => !s.completedAt && !s.abandonedAt) ?? [];
    return open[open.length - 1];
  }, [state.sessions]);

  const durationSec = (state.settings?.focusDurationMin ?? 25) * 60;
  const remaining = active ? F.remainingSec(active) : durationSec;
  const progress = active ? F.progress(active) : 0;

  // رندر هر ثانیه فقط وقتی چیزی در جریان است
  useEffect(() => {
    if (!active && !breakUntil) return;
    const id = window.setInterval(() => forceTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [active, breakUntil]);

  // برگشتن به تب: عدد باید فوراً درست شود
  useEffect(() => {
    const onVis = () => forceTick((n) => n + 1);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, []);

  // وضعیت واقعی از روی داده، نه از روی state محلی
  useEffect(() => {
    if (breakUntil) {
      setPhase(Date.now() < breakUntil ? "break" : "idle");
      if (Date.now() >= breakUntil) setBreakUntil(null);
      return;
    }
    if (!active) { setPhase("idle"); return; }
    if (F.isPaused(active)) { setPhase("paused"); return; }
    if (F.remainingSec(active) === 0) { setPhase("done"); return; }
    setPhase("running");
  }, [active, remaining, breakUntil]);

  // جلسه به پایان رسید — یک بار ثبت می‌شود
  const finishedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!active || F.remainingSec(active) > 0 || active.completedAt) return;
    if (finishedRef.current === active.id) return;
    finishedRef.current = active.id;
    const done = F.complete(active);
    dispatch({ type: "patchSession", session: done, day: today });
    haptic("success");
    void wake.release();
    setClosingConfirmation(false);
  }, [active, remaining, dispatch, today]);

  // قفل روشن ماندن صفحه فقط وقتی جلسه در جریان است
  useEffect(() => {
    if (phase === "running") {
      void wake.acquire();
      setClosingConfirmation(true);
    } else {
      void wake.release();
    }
    return wake.reacquireOnVisible(() => phase === "running");
  }, [phase]);

  /**
   * ریزحرکت: هر ۴۰ تا ۹۰ ثانیه سهیل کوتاه «فکر می‌کند».
   * بدون این، حلقهٔ مطالعه بعد از چند دقیقه مثل یک GIF تکراری می‌شود.
   */
  useEffect(() => {
    window.clearTimeout(microRef.current);
    if (phase !== "running") { setMicroPose(null); return; }
    const schedule = () => {
      const delay = 40_000 + Math.random() * 50_000;
      microRef.current = window.setTimeout(() => {
        setMicroPose("thinking");
        window.setTimeout(() => { setMicroPose(null); schedule(); }, 2600);
      }, delay);
    };
    schedule();
    return () => window.clearTimeout(microRef.current);
  }, [phase]);

  const start = useCallback((label?: string, taskId?: string) => {
    finishedRef.current = null;
    dispatch({ type: "startSession", durationSec, label, taskId });
    haptic("tap");
  }, [dispatch, durationSec]);

  const pause = useCallback(() => {
    if (!active) return;
    dispatch({ type: "patchSession", session: F.pause(active), day: today });
    haptic("tap");
  }, [active, dispatch, today]);

  const resume = useCallback(() => {
    if (!active) return;
    dispatch({ type: "patchSession", session: F.resume(active), day: today });
    haptic("tap");
  }, [active, dispatch, today]);

  const abandon = useCallback(() => {
    if (!active) return;
    dispatch({ type: "patchSession", session: F.abandon(active), day: today });
    haptic("warning");
  }, [active, dispatch, today]);

  const startBreak = useCallback(() => {
    setBreakUntil(Date.now() + (state.settings?.breakDurationMin ?? 5) * 60_000);
  }, [state.settings?.breakDurationMin]);

  const dismiss = useCallback(() => {
    setBreakUntil(null);
    finishedRef.current = null;
  }, []);

  const breakRemaining = breakUntil ? Math.max(0, Math.round((breakUntil - Date.now()) / 1000)) : 0;

  const pose: Pose =
    phase === "running" ? (microPose ?? "studying")
    : phase === "paused" ? "thinking"
    : phase === "done" ? "celebrate"
    : phase === "break" ? "break"
    : "idle";

  return {
    phase, pose, active,
    remaining: phase === "break" ? breakRemaining : remaining,
    clock: F.formatClock(phase === "break" ? breakRemaining : remaining),
    progress,
    start, pause, resume, abandon, startBreak, dismiss,
  };
}
